import React, { useEffect, useMemo, useState } from "react";
import { LuChevronLeft, LuChevronRight } from "react-icons/lu";
import DateFilterSelector from "../date-filter-selector";

const FILTER_PRESETS = {
  TODAY: "today",
  YESTERDAY: "yesterday",
  LAST_7_DAYS: "last_7_days",
};

const FILTER_OPTIONS = [
  { value: FILTER_PRESETS.TODAY, label: "Hoy" },
  { value: FILTER_PRESETS.YESTERDAY, label: "Ayer" },
  { value: FILTER_PRESETS.LAST_7_DAYS, label: "Ultimos 7 dias" },
];

const toDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const addDays = (date, days) => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
};

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const getPresetAnchorDate = (preset, today) => {
  if (preset === FILTER_PRESETS.YESTERDAY) {
    return addDays(today, -1);
  }

  return today;
};

const getDateRange = (date, preset) => {
  const anchorDate = startOfDay(date);

  if (preset === FILTER_PRESETS.LAST_7_DAYS) {
    return {
      startDate: addDays(anchorDate, -6),
      endDate: anchorDate,
    };
  }

  return {
    startDate: anchorDate,
    endDate: anchorDate,
  };
};

const formatShortDateLabel = (date) =>
  new Intl.DateTimeFormat("es-PE", {
    day: "numeric",
    month: "short",
  }).format(date);

const formatRangeLabel = (startDate, endDate, preset) => {
  if (preset === FILTER_PRESETS.LAST_7_DAYS) {
    return `${formatShortDateLabel(startDate)} - ${formatShortDateLabel(endDate)}`;
  }

  return formatShortDateLabel(endDate);
};

const buildDateValues = (startDate, endDate) => {
  const values = [];

  for (
    let currentDate = new Date(startDate);
    currentDate.getTime() <= endDate.getTime();
    currentDate = addDays(currentDate, 1)
  ) {
    values.push(toDateInputValue(currentDate));
  }

  return values;
};

const DateFilter = ({
  onChange,
  defaultPreset = FILTER_PRESETS.TODAY,
  options = FILTER_OPTIONS,
}) => {
  const today = useMemo(() => startOfDay(new Date()), []);
  const [selectedPreset, setSelectedPreset] = useState(defaultPreset);
  const [selectedDate, setSelectedDate] = useState(getPresetAnchorDate(defaultPreset, today));
  const [isSelectorOpen, setIsSelectorOpen] = useState(false);

  const { startDate, endDate } = useMemo(
    () => getDateRange(selectedDate, selectedPreset),
    [selectedDate, selectedPreset]
  );

  const label = useMemo(
    () => formatRangeLabel(startDate, endDate, selectedPreset),
    [endDate, selectedPreset, startDate]
  );

  const canGoForward = endDate.getTime() < today.getTime();

  useEffect(() => {
    onChange?.({
      preset: selectedPreset,
      selectedDate,
      startDate,
      endDate,
      startDateValue: toDateInputValue(startDate),
      endDateValue: toDateInputValue(endDate),
      label,
      dates: buildDateValues(startDate, endDate),
    });
  }, [endDate, label, onChange, selectedDate, selectedPreset, startDate]);

  const goToPrevious = () => {
    setSelectedDate((currentDate) =>
      addDays(currentDate, selectedPreset === FILTER_PRESETS.LAST_7_DAYS ? -7 : -1)
    );
  };

  const goToNext = () => {
    setSelectedDate((currentDate) =>
      addDays(currentDate, selectedPreset === FILTER_PRESETS.LAST_7_DAYS ? 7 : 1)
    );
  };

  const handleSelectorSelect = (preset) => {
    setSelectedPreset(preset);
    setSelectedDate(getPresetAnchorDate(preset, today));
    setIsSelectorOpen(false);
  };

  return (
    <div className="flex items-start w-full gap-2">
      <div className="flex items-center flex-1 min-w-0 px-1 h-[48px] bg-slate-50 border border-slate-200 bg-white rounded-[24px]">
        <button
          className="flex items-center justify-center w-11 h-11 rounded-2xl text-slate-500 active:scale-95 shrink-0"
          onClick={goToPrevious}
          type="button"
          aria-label="Periodo anterior"
        >
          <LuChevronLeft size={20} />
        </button>

        <div className="flex-1 min-w-0 px-2 text-center">
          <span className="block text-[15px] font-semibold text-slate-800 truncate capitalize">
            {label}
          </span>
        </div>

        <button
          className="flex items-center justify-center w-11 h-11 rounded-2xl text-slate-500 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
          onClick={goToNext}
          disabled={!canGoForward}
          type="button"
          aria-label="Periodo siguiente"
        >
          <LuChevronRight size={20} />
        </button>
      </div>

      <DateFilterSelector
        isOpen={isSelectorOpen}
        selectedValue={selectedPreset}
        options={options}
        onOpen={() => setIsSelectorOpen(true)}
        onClose={() => setIsSelectorOpen(false)}
        onSelect={handleSelectorSelect}
      />
    </div>
  );
};

export default DateFilter;
